import type { Color } from "../../common/color.js";
import type { Control } from "../core/Control.js";
import { RenderTarget } from "../../engine/RenderTarget.js";
import type { Theme } from "../themes/Theme.js";
import type { GlyphAtlas } from "./GlyphAtlas.js";
import type { GpuContext } from "../../engine/GpuContext.js";
import { QuadBatcher } from "./QuadBatcher.js";
import { RenderContext } from "./RenderContext.js";

/**
 * Controls painted into a texture instead of onto the canvas: the page as a picture.
 *
 * What a shader over the UI needs and drawing straight to the canvas cannot give it - a burn that eats
 * the page being left, a blur of the page behind a lightbox. The layer has its own batch and its own
 * target, so the quad material draws into it unchanged; glyphs come from the desktop's atlas, so text
 * costs nothing new.
 *
 * Painting happens while the desktop is walking its controls, before it draws its own quads, which is
 * the same window `Viewport3D` renders its scene in.
 */
export class PaintLayer {
  readonly gpu: GpuContext;
  readonly rc: RenderContext;
  #batcher: QuadBatcher;
  #target: RenderTarget | null = null;

  constructor(gpu: GpuContext, atlas: GlyphAtlas) {
    this.gpu = gpu;
    this.#batcher = new QuadBatcher(gpu);
    this.rc = new RenderContext(gpu, this.#batcher, atlas);
  }

  /** The last picture painted, or null before the first. */
  get target(): RenderTarget | null {
    return this.#target;
  }

  /**
   * Paint `width` x `height` logical pixels into the layer's texture at `scale` device pixels a
   * logical one. `draw` paints at the layer's origin; `paintControl` is the usual body.
   */
  paint(width: number, height: number, scale: number, draw: (rc: RenderContext) => void, clear: Color | null = null): RenderTarget {
    const pixelWidth = Math.max(1, Math.round(width * scale));
    const pixelHeight = Math.max(1, Math.round(height * scale));
    if (!this.#target) {
      this.#target = new RenderTarget(this.gpu, { width: pixelWidth, height: pixelHeight, label: "webwin.layer" });
    } else {
      this.#target.resize(pixelWidth, pixelHeight);
    }
    const target = this.#target;
    this.rc.begin({ width, height, pixelWidth: target.width, pixelHeight: target.height, dpr: scale });
    draw(this.rc);
    target.begin(clear ?? { r: 0, g: 0, b: 0, a: 0 });
    this.rc.flush();
    return target;
  }

  /** Paint one control, and everything in it, as it would appear on the canvas. */
  paintControl(control: Control, theme: Theme, scale: number, clear: Color | null = null): RenderTarget {
    return this.paint(control.width, control.height, scale, (rc) => control.paint(rc, theme), clear);
  }

  dispose(): void {
    this.#target?.dispose();
    this.#target = null;
    this.#batcher.dispose();
  }
}
